import { FC, useEffect, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMeProviderSignUpConfigLazyQuery } from '../../generated/graphql';
import {
  matchHomecomingError,
  HomecomingError,
  isAuth0InvalidStateError,
} from '../../lib/errors';
import { useHomecomingLoader } from '../hooks/useHomecomingLoader';
import { logger } from '../../lib/logger';

const AuthCallback: FC = () => {
  const navigate = useNavigate();
  const {
    getAccessTokenSilently,
    handleRedirectCallback,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const { saveToken } = useAuth();
  const HomecomingLoader = useHomecomingLoader();

  const [meProviderSignUpConfigQuery] = useMeProviderSignUpConfigLazyQuery();

  const effectRan = useRef(false);
  const RECOVERY_ATTEMPTED_KEY = 'auth_callback_recovery_attempted';
  const RECOVERY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    // This check is critical to prevent React's strict mode from causing the effect to run twice.
    // The second run will always fail.
    if (effectRan.current) {
      return;
    }
    effectRan.current = true;

    // Clear any stale recovery marker from previous sessions (older than 5 minutes)
    const recoveryMarker = sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY);
    if (recoveryMarker) {
      const recoveryTimestamp = parseInt(recoveryMarker, 10);
      const now = Date.now();
      if (now - recoveryTimestamp > RECOVERY_TIMEOUT_MS) {
        sessionStorage.removeItem(RECOVERY_ATTEMPTED_KEY);
      }
    }

    (async () => {
      let from = '/';
      try {
        const { appState } = await handleRedirectCallback();
        if (appState?.from) {
          from = appState.from;
        }

        const accessToken = await getAccessTokenSilently();

        if (!accessToken) {
          throw new Error('No access token');
        }

        saveToken(accessToken);

        const { data } = await meProviderSignUpConfigQuery();
        if (data?.meProvider?.signUpConfig?.isCommunityHub) {
          navigate('/community', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } catch (error) {
        // Invalid/missing transaction state (e.g. callback opened in a different
        // browser, cookies blocked by ITP, or a duplicate callback run) is
        // recoverable: start a fresh login so Auth0 creates a new transaction,
        // instead of bouncing to "/" which can loop back to a broken callback.
        if (isAuth0InvalidStateError(error)) {
          // Check if we've already attempted recovery in this session to prevent infinite loops
          const alreadyRecovered = sessionStorage.getItem(
            RECOVERY_ATTEMPTED_KEY,
          );
          if (alreadyRecovered) {
            // Recovery has already been attempted but failed again - this is a persistent issue.
            // Log as an error and redirect to login without appState to force a fresh flow.
            logger.error(
              'AUTH_CALLBACK_RECOVERY_FAILED: auth callback invalid state persists after recovery attempt',
              {
                error:
                  error instanceof Error ? error : new Error(String(error)),
              },
            );
            // Keep the marker set to prevent infinite loops.
            // The marker will naturally expire after RECOVERY_TIMEOUT_MS (5 minutes).
            await loginWithRedirect();
            return;
          }

          // First time seeing this error - attempt recovery.
          // Log as a breadcrumb (not an error event) and start a fresh login so Auth0 creates a new transaction.
          logger.warn(
            'AUTH_CALLBACK_INVALID_STATE: auth callback received invalid state; attempting recovery',
            error instanceof Error ? error.message : String(error),
          );
          // Mark that we've attempted recovery so we don't loop infinitely
          sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY, String(Date.now()));
          await loginWithRedirect({ appState: { from } });
          return;
        }

        // Only logout on auth failure (e.g. TokenInvalid). Other errors (e.g. DB/GraphQL)
        // would otherwise cause a redirect loop: callback -> query fails -> logout -> login again.
        const isAuthError = matchHomecomingError(
          error as ApolloError,
          HomecomingError.TokenInvalid,
        );

        // Log to Sentry with appropriate error ID
        logger.error('Error handling auth redirect callback', {
          errorId: isAuthError
            ? 'AUTH_CALLBACK_AUTH_FAILED'
            : 'AUTH_CALLBACK_ERROR',
          isAuthError,
          error: error instanceof Error ? error : new Error(String(error)),
        });

        if (isAuthError) {
          await logout();
        } else {
          navigate('/', { replace: true });
        }
      }
    })();
  }, []);

  return HomecomingLoader;
};

export default AuthCallback;
